import React, { useEffect, useRef, useState } from 'react'
import { Keyboard, StyleSheet, View, Text } from 'react-native'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import firebase from 'firebase/compat/app'
import ReactQuill from 'react-quill-new'

import CustomTextInput3 from '../../../Feeds/CommentsTextInput/CustomTextInput3'
import { colors } from '../../../styles/global'
import { processPastedTextWithBreakLines, TASK_THEME } from '../../../Feeds/CommentsTextInput/textInputHelper'
import { STAYWARD_COMMENT, updateNewAttachmentsData } from '../../../Feeds/Utils/HelperFunctions'
import { MENTION_MODAL_ID } from '../../../ModalsManager/modalsManager'
import { translate } from '../../../../i18n/TranslationService'
import { updateXpByCommentInChat } from '../../../../utils/Levels'
import Backend from '../../../../utils/BackendBridge'
import { checkIsLimitedByXp } from '../../../Premium/PremiumHelper'
import {
    setAssistantEnabled,
    setDisableAutoFocusInChat,
    setMainChatEditor,
    setQuotedNoteText,
    setQuotedText,
    setTriggerChatDraft,
    setTriggerChatSubmit,
} from '../../../../redux/actions'
import ChatInputButtons from './ChatInputButtons'
import { CHAT_INPUT_LIMIT_IN_CHARACTERS } from '../../../../utils/assistantHelper'
import { createObjectMessage } from '../../../../utils/backends/Chats/chatsComments'
import { resolveAssistantForProjectObject } from '../../../AdminPanel/Assistants/assistantsHelper'
import AttachmentDropZone from '../../../Feeds/CommentsTextInput/AttachmentDropZone'
import { selectAssistantEnabledFor } from '../../Utils/assistantEnabledScope'
import { endBotSpinnerWait } from '../../Utils/botSpinnerTrigger'
import useHomeIndicatorLift from '../../../../hooks/useHomeIndicatorLift'
import { readCommentDraft, saveCommentDraft, clearCommentDraft } from '../../../../utils/commentDraftStore'
import { CHAT_COMPOSER_LIFT, getChatComposerLift } from '../chatComposerLayout'

const Delta = ReactQuill.Quill.import('delta')

export default function ChatInput({
    chat,
    parentObject,
    editing,
    initialText,
    projectId,
    chatTitle,
    members,
    containerStyle,
    messageId,
    closeEditMode,
    creatorId,
    setWaitingForBotAnswer,
    creatorData,
    assistantId,
    setAssistantId,
    objectType,
    setAmountOfNewCommentsToHighligth,
    onMessageSent,
    autoFocus = true,
}) {
    const dispatch = useDispatch()
    const homeIndicatorLift = useHomeIndicatorLift()
    const isMentionModalOpen = useSelector(state => state.openModals[MENTION_MODAL_ID])
    const quotedNoteText = useSelector(state => state.quotedNoteText)
    const quotedText = useSelector(state => state.quotedText)
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const gold = useSelector(state => state.loggedUser.gold)
    const disableAutoFocusInChat = useSelector(state => state.disableAutoFocusInChat)
    // Read the global flag through the chat scope: a flag armed for a different chat must not
    // reactivate the assistant here. `isAssistantActive` ORs this with the persisted state and
    // feeds `explicitAssistantEnabled`, which OVERRIDES the object's own `isAssistantEnabled`
    // inside `createObjectMessage` — so an unscoped leak would fire a real (Gold-spending)
    // assistant run in a chat where the user had switched the assistant off (AT-2084).
    const assistantEnabled = useSelector(state => selectAssistantEnabledFor(state, projectId, chat?.id))
    useSelector(
        state => ({
            projectAssistants: state.projectAssistants,
            globalAssistants: state.globalAssistants,
            defaultAssistant: state.defaultAssistant,
            loggedUserProjects: state.loggedUserProjects,
            loggedUserProjectsMap: state.loggedUserProjectsMap,
        }),
        shallowEqual
    )
    const [autoFocusDisabled, setAutoFocusDisabled] = useState(disableAutoFocusInChat)
    const [chatEditor, setChatEditor] = useState(null)
    const draftContext = { userId: loggedUserId, projectId, objectId: messageId || chat.id, objectType: chat.type }
    const [recoveredDraft] = useState(() => readCommentDraft(draftContext)?.comment)
    const [inputText, setInputText] = useState(recoveredDraft || '')
    const [sendError, setSendError] = useState('')
    const [isSending, setIsSending] = useState(false)
    const sendingRef = useRef(false)
    const persistInput = text => {
        setInputText(text.trim())
        if (!saveCommentDraft(draftContext, { comment: text })) {
            setSendError(translate('Could not save on this device. Keep this window open and try again.'))
        }
    }
    const [inputCursorIndex, setInputCursorIndex] = useState(0)
    const [showRunOutGoalModal, setShowRunOutGoalModal] = useState(false)
    const inputRef = useRef(null)
    const isShiftPressed = useRef(false)
    const displayedAssistantId = resolveAssistantForProjectObject(projectId, assistantId)?.uid || assistantId
    const selectedAssistantIdRef = useRef(displayedAssistantId)

    const { id: objectId, type: chatType } = chat

    const hasExplicitChatAssistantState = typeof chat?.isAssistantEnabled === 'boolean'
    const hasParentAssistantState = typeof parentObject?.isAssistantEnabled === 'boolean'
    const persistedAssistantActive = hasExplicitChatAssistantState
        ? chat.isAssistantEnabled === true
        : hasParentAssistantState
          ? parentObject.isAssistantEnabled === true
          : false
    // The composer's own assistant button writes the thread's `isAssistantEnabled` and shows the
    // new state at once, but `chat` / `parentObject` only follow once the host's listener delivers
    // the write — and some hosts never do (AT-2886: the task DV had silently dropped its task
    // listener on the first tab change). The send decision below must use exactly the state the
    // button shows, so a toggle made here wins until the persisted value moves on its own.
    const [assistantToggle, setAssistantToggle] = useState(null)
    const toggleIsCurrent = !!assistantToggle && assistantToggle.basis === persistedAssistantActive
    const threadAssistantActive = toggleIsCurrent ? assistantToggle.enabled : persistedAssistantActive
    const isAssistantActive = threadAssistantActive || assistantEnabled
    const explicitAssistantEnabled =
        toggleIsCurrent || hasExplicitChatAssistantState || hasParentAssistantState || assistantEnabled
            ? isAssistantActive
            : null
    const onAssistantToggled = updatedObject => {
        if (typeof updatedObject?.isAssistantEnabled !== 'boolean') return
        setAssistantToggle({ enabled: updatedObject.isAssistantEnabled, basis: persistedAssistantActive })
    }
    const disabledEdition = editing && loggedUserId !== creatorId
    const shouldAutoFocus = autoFocus && !disableAutoFocusInChat && !autoFocusDisabled

    const updateSelectedAssistant = selectedAssistantId => {
        selectedAssistantIdRef.current = selectedAssistantId
        setAssistantId?.(selectedAssistantId)
    }

    useEffect(() => {
        selectedAssistantIdRef.current = displayedAssistantId
    }, [displayedAssistantId])

    const updateEditor = editor => {
        if (!editing) dispatch(setMainChatEditor(editor))
        setChatEditor(editor)
    }

    useEffect(() => {
        if (!editing) {
            return () => {
                dispatch(setMainChatEditor(null))
            }
        }
    }, [])

    const onEdit = async event => {
        event?.preventDefault?.()
        if (sendingRef.current) return
        if (disabledEdition || initialText.trim() === inputText.trim()) {
            clearCommentDraft(draftContext, inputText)
            closeEditMode()
        } else {
            sendingRef.current = true
            setIsSending(true)
            setSendError('')
            try {
                if (!saveCommentDraft(draftContext, { comment: inputText })) {
                    throw new Error(translate('Could not save on this device. Keep this window open and try again.'))
                }
                const commentWithAttachments = await updateNewAttachmentsData(projectId, inputText)
                await createObjectMessage(
                    projectId,
                    objectId,
                    commentWithAttachments,
                    chatType,
                    chatType === 'tasks' ? STAYWARD_COMMENT : null,
                    messageId,
                    initialText,
                    false, // skipAssistantTrigger
                    explicitAssistantEnabled
                )
                clearCommentDraft(draftContext, inputText)
                setAmountOfNewCommentsToHighligth(0)
                closeEditMode()
            } catch (error) {
                setSendError(error.message || translate('Comment could not be sent. Your text is still here.'))
            } finally {
                sendingRef.current = false
                setIsSending(false)
            }
        }
    }

    const onSubmit = async eventOrText => {
        eventOrText?.preventDefault?.()
        if (sendingRef.current) return
        const textToSubmit = typeof eventOrText === 'string' ? eventOrText : inputText
        if (!textToSubmit?.trim()) return
        if (isAssistantActive && gold === 0) {
            setShowRunOutGoalModal(true)
            dispatch(setAssistantEnabled(false))
            return
        }
        if (checkIsLimitedByXp(projectId)) return
        sendingRef.current = true
        const submittedAssistantId = selectedAssistantIdRef.current
        setIsSending(true)
        setSendError('')
        try {
            if (!saveCommentDraft(draftContext, { comment: textToSubmit })) {
                throw new Error(translate('Could not save on this device. Keep this window open and try again.'))
            }
            if (isAssistantActive) setWaitingForBotAnswer(true)
            const comment = await updateNewAttachmentsData(projectId, textToSubmit)
            await createObjectMessage(
                projectId,
                objectId,
                comment,
                chatType,
                chatType === 'tasks' ? STAYWARD_COMMENT : null,
                null,
                null,
                false,
                explicitAssistantEnabled,
                submittedAssistantId
            )
            clearCommentDraft(draftContext, textToSubmit)
            inputRef.current?.clear()
            setInputText('')
            onMessageSent?.()
            setAmountOfNewCommentsToHighligth(0)
            updateXpByCommentInChat(loggedUserId, firebase, Backend.getDb(), projectId)
        } catch (error) {
            if (isAssistantActive) endBotSpinnerWait(projectId, objectId)
            setWaitingForBotAnswer(false)
            setSendError(error.message || translate('Comment could not be sent. Your text is still here.'))
        } finally {
            sendingRef.current = false
            setIsSending(false)
        }
    }

    const onKeyDown = event => {
        if (inputRef.current.isFocused()) {
            if (event.key === 'Enter' && !isShiftPressed.current && !!inputText && !isMentionModalOpen) {
                editing ? onEdit(event) : onSubmit(event)
            }
            if (event.key === 'Shift') {
                isShiftPressed.current = true
            }
        }
    }

    const onKeyUp = event => {
        if (inputRef.current.isFocused()) {
            if (event.key === 'Shift') {
                isShiftPressed.current = false
            }
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('keyup', onKeyUp)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('keyup', onKeyUp)
        }
    })

    useEffect(() => {
        return () => {
            if (!editing) {
                setTimeout(() => {
                    dispatch(setQuotedNoteText(''))
                })
                dispatch(setQuotedText(null))
            }
        }
    }, [])

    useEffect(() => {
        if (disableAutoFocusInChat) {
            setAutoFocusDisabled(true)
            dispatch(setDisableAutoFocusInChat(false))
        }
    }, [disableAutoFocusInChat])

    useEffect(() => {
        if (shouldAutoFocus) return

        const blurTimeout = setTimeout(() => {
            inputRef.current?.blur()
            Keyboard.dismiss()
        })

        return () => clearTimeout(blurTimeout)
    }, [shouldAutoFocus])

    useEffect(() => {
        const focusTimeout = setTimeout(() => {
            if (quotedText || shouldAutoFocus) inputRef.current?.focus()
        })
        return () => clearTimeout(focusTimeout)
    }, [quotedText, shouldAutoFocus])

    useEffect(() => {
        if (chatEditor && quotedText) {
            const regex = /\[quote\](.*?)\[quote\]/g
            const HELPER_KEY = '4FFG345GKSL23834MDF47SDF83JSDFKCNM27234SFKK475'

            let cleanedText = quotedText.text.replaceAll('\n', HELPER_KEY)
            cleanedText = cleanedText.replaceAll(regex, '')
            cleanedText = cleanedText.replaceAll(HELPER_KEY, '\n')
            cleanedText = cleanedText.replaceAll('\n\n', '\n').trim()

            const text = `[quote][header]${quotedText.userName}[header]\n${cleanedText}[quote]`
            const delta = processPastedTextWithBreakLines(
                text,
                Delta,
                projectId,
                inputRef.current.getEditorId(),
                loggedUserId,
                false,
                null,
                chatEditor,
                false,
                null,
                false
            )
            chatEditor.setContents(delta)
            chatEditor.updateContents({ ops: [{ retain: chatEditor.getLength() }, { insert: '\n' }] })
            chatEditor.setSelection(chatEditor.getLength(), 0)
        }
    }, [quotedText])

    const triggerChatSubmit = useSelector(state => state.triggerChatSubmit)
    const triggerChatDraft = useSelector(state => state.triggerChatDraft)

    useEffect(() => {
        if (triggerChatSubmit && chatEditor) {
            // Only process if the trigger is for the current chat or generic (null ID check if needed, but for now we assume global trigger is consumed by active chat)
            // Ideally we should check if this chat is the intended target, but since we navigate to it, it should be fine.
            // Using a specific payload structure: { text: "prompt", chatId: "..." } would be safer, but for now let's trust the navigation.

            if (triggerChatSubmit.text) {
                // Set text first
                const Delta = ReactQuill.Quill.import('delta')
                chatEditor.setContents(new Delta().insert(triggerChatSubmit.text + '\n'))

                // Update input text state as well since onSubmit uses it
                setInputText(triggerChatSubmit.text)

                // Trigger submit
                // We need to verify if we need to call updateNewAttachmentsData explicitly or if onSubmit handles it.
                // onSubmit handles it.

                // We fake an event object or make it optional in onSubmit
                const textToSubmit = triggerChatSubmit.text
                setTimeout(() => {
                    onSubmit(textToSubmit)
                }, 100)
            }

            // Clear the trigger
            dispatch(setTriggerChatSubmit(null))
        }
    }, [triggerChatSubmit, chatEditor])

    useEffect(() => {
        if (triggerChatDraft && chatEditor) {
            const hasTargetChat = !triggerChatDraft.chatId || triggerChatDraft.chatId === objectId
            if (hasTargetChat && triggerChatDraft.text) {
                const Delta = ReactQuill.Quill.import('delta')
                chatEditor.setContents(new Delta().insert(triggerChatDraft.text + '\n'))
                setInputText(triggerChatDraft.text)
                chatEditor.setSelection(triggerChatDraft.text.length, 0)
                setTimeout(() => inputRef.current?.focus(), 50)
            }

            if (hasTargetChat) {
                dispatch(setTriggerChatDraft(null))
            }
        }
    }, [triggerChatDraft, chatEditor, objectId])

    const handlePerplexityResponse = (editor, content) => {
        if (!editor || !content) {
            console.log('handlePerplexityResponse: Missing editor or content', {
                hasEditor: !!editor,
                hasContent: !!content,
            })
            return
        }

        console.log('handlePerplexityResponse: Processing content:', content)
        const Delta = ReactQuill.Quill.import('delta')
        const delta = new Delta()

        // The content should already be in HTML format from the PerplexityClient
        // We just need to ensure it's properly formatted for Quill
        console.log('Converting content to Quill delta format...')
        const tempDelta = editor.clipboard.convert({ html: content })
        console.log('Converted delta:', tempDelta)

        // Process each operation in the delta
        tempDelta.ops.forEach((op, index) => {
            console.log(`Processing delta operation ${index}:`, op)
            if (op.insert) {
                // Handle code blocks
                if (op.attributes && op.attributes['code-block']) {
                    console.log('Processing code block:', op.insert)
                    delta.insert(op.insert, { 'code-block': true })
                }
                // Handle inline code
                else if (op.attributes && op.attributes.code) {
                    console.log('Processing inline code:', op.insert)
                    delta.insert(op.insert, { code: true })
                }
                // Handle links
                else if (op.attributes && op.attributes.link) {
                    console.log('Processing link:', { text: op.insert, link: op.attributes.link })
                    delta.insert(op.insert, { link: op.attributes.link })
                }
                // Handle other formatted text
                else {
                    console.log('Processing formatted text:', { text: op.insert, attributes: op.attributes || {} })
                    delta.insert(op.insert, op.attributes || {})
                }
            }
        })

        // Ensure proper spacing
        if (!content.endsWith('\n')) {
            console.log('Adding trailing newline')
            delta.insert('\n')
        }

        // Update the editor content
        console.log('Final delta to be applied:', delta)
        editor.updateContents(delta, 'user')
        console.log('Editor content updated')
    }

    return (
        <AttachmentDropZone
            testID="full-chat-image-drop-zone"
            style={[
                localStyles.inputContainer,
                // Keep the floating composer clear of the iOS home indicator
                // now that #root no longer reserves that region (0 elsewhere,
                // and 0 while the keyboard is open — see useHomeIndicatorLift).
                homeIndicatorLift > 0 && { bottom: getChatComposerLift(homeIndicatorLift) },
                containerStyle,
            ]}
            disabled={disabledEdition}
            editor={chatEditor}
            inputCursorIndex={inputCursorIndex}
            projectId={projectId}
            setInputCursorIndex={setInputCursorIndex}
        >
            {!!sendError && (
                <Text accessibilityRole="alert" style={{ color: colors.Text02 }}>
                    {sendError}
                </Text>
            )}
            <CustomTextInput3
                ref={inputRef}
                placeholder={translate('Type to add new comment')}
                placeholderTextColor={colors.Text03}
                onChangeText={persistInput}
                autoFocus={shouldAutoFocus}
                projectId={projectId}
                externalAlignment={localStyles.textInputAlignment}
                containerStyle={localStyles.textInputContainer}
                styleTheme={TASK_THEME}
                setInputCursorIndex={setInputCursorIndex}
                setEditor={updateEditor}
                otherFormats={['image', 'attachment', 'customImageFormat', 'videoFormat']}
                disabledEdition={disabledEdition || isSending}
                initialTextExtended={recoveredDraft || initialText || quotedNoteText}
                keepBreakLines={true}
                characterLimit={CHAT_INPUT_LIMIT_IN_CHARACTERS}
                setShowRunOutGoalModal={setShowRunOutGoalModal}
                chatAssistantData={{ objectId, objectAssistantId: assistantId, objectType: chatType }}
                setAssistantId={updateSelectedAssistant}
                // Same as the assistant line: the mic is pinned on in the chat composer rather
                // than revealed on hover/focus (AT-2355). The strip it sits in is reserved
                // whenever dictation is enabled, so nothing reflows.
                alwaysShowDictation={true}
                // Push-to-talk (AT-2405). This composer submits through a document-level keydown
                // listener rather than the generic `forceTriggerEnterActionForBreakLines`, so it
                // has to hand the send action over explicitly. The text passed in is the whole
                // composer content (existing draft + transcript), and `onSubmit` already accepts a
                // string precisely so a programmatic send does not race the `inputText` state.
                onDictationSubmit={editing ? undefined : text => text && onSubmit(text)}
            />
            <ChatInputButtons
                projectId={projectId}
                chatTitle={chatTitle}
                members={members}
                onSubmit={editing ? onEdit : onSubmit}
                inputText={inputText}
                inputCursorIndex={inputCursorIndex}
                editor={chatEditor}
                initialText={initialText}
                editing={editing}
                disabledEdition={disabledEdition || isSending}
                closeEditMode={closeEditMode}
                creatorId={creatorId}
                inputRef={inputRef}
                setShowRunOutGoalModal={setShowRunOutGoalModal}
                showRunOutGoalModal={showRunOutGoalModal}
                creatorData={creatorData}
                assistantId={assistantId}
                setAssistantId={updateSelectedAssistant}
                objectId={chat.id}
                objectType={objectType}
                assistantEnabled={isAssistantActive}
                onAssistantToggled={onAssistantToggled}
            />
        </AttachmentDropZone>
    )
}

const localStyles = StyleSheet.create({
    inputContainer: {
        // Shared, because the "New message ↓" pill has to stay clear of where this actually paints
        // — a relative offset leaves the layout box behind. See chatComposerLayout.js.
        bottom: CHAT_COMPOSER_LIFT,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: colors.Grey200,
        borderRadius: 4,
        boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
    },
    textInputContainer: {
        marginTop: 2,
        marginBottom: 12,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        minHeight: 40, // 59 - (7 + 12)
        marginLeft: 17,
        // 8, not TaskInput's 40: that margin clears the assignee avatar in the task row, but the
        // chat input has nothing on its right (its buttons live below), and the dictation mic
        // anchors to the frame's right edge — 40 left it floating mid-row.
        marginRight: 8,
    },
    textInputAlignment: {
        paddingLeft: 0,
        paddingRight: 0,
    },
})
